import { z } from 'zod';
import { BillingIntervalSchema } from '../types/subscriptions/enums';
import { PositiveMoneyCentsSchema } from '../types/sale/v4/execution';
import { PublicExperienceProviderSchema } from './enums';
import { PublicExperienceCurrencySchema } from './offer';
import { IsoTimestampSchema, StripeSubscriptionIdSchema } from './primitives';
/**
 * Public Experiences — the live recurring billing read model.
 *
 * Ongoing subscription state is NEVER stored locally; it is read live from the
 * provider when an admin opens a submission. That keeps one source of truth and
 * avoids a billing mirror that silently drifts.
 *
 * These values are normalized PointCraft values, not Stripe strings, and they are
 * read-only: nothing here authorizes a charge.
 */
/**
 * Normalized subscription status. Mapped from the provider's status by the
 * backend; anything unrecognized maps to `UNKNOWN` rather than being dropped, so
 * a new provider status can never be displayed as "active".
 */
export var LiveRecurringStatusEnum;
(function (LiveRecurringStatusEnum) {
    LiveRecurringStatusEnum["ACTIVE"] = "ACTIVE";
    LiveRecurringStatusEnum["TRIALING"] = "TRIALING";
    LiveRecurringStatusEnum["PAST_DUE"] = "PAST_DUE";
    LiveRecurringStatusEnum["UNPAID"] = "UNPAID";
    LiveRecurringStatusEnum["PAUSED"] = "PAUSED";
    LiveRecurringStatusEnum["INCOMPLETE"] = "INCOMPLETE";
    LiveRecurringStatusEnum["CANCELED"] = "CANCELED";
    LiveRecurringStatusEnum["UNKNOWN"] = "UNKNOWN";
})(LiveRecurringStatusEnum || (LiveRecurringStatusEnum = {}));
export const LiveRecurringStatusSchema = z.nativeEnum(LiveRecurringStatusEnum);
/**
 * Normalized status of the most recent invoice. An invoice is not a payment:
 * `OPEN` means billed and unpaid, only `PAID` means money moved.
 */
export var LastInvoiceStatusEnum;
(function (LastInvoiceStatusEnum) {
    LastInvoiceStatusEnum["DRAFT"] = "DRAFT";
    LastInvoiceStatusEnum["OPEN"] = "OPEN";
    LastInvoiceStatusEnum["PAID"] = "PAID";
    LastInvoiceStatusEnum["UNCOLLECTIBLE"] = "UNCOLLECTIBLE";
    LastInvoiceStatusEnum["VOID"] = "VOID";
    LastInvoiceStatusEnum["UNKNOWN"] = "UNKNOWN";
})(LastInvoiceStatusEnum || (LastInvoiceStatusEnum = {}));
export const LastInvoiceStatusSchema = z.nativeEnum(LastInvoiceStatusEnum);
/** Why a live read could not produce a summary. */
export var BillingUnavailableReasonEnum;
(function (BillingUnavailableReasonEnum) {
    /** No subscription is linked yet (checkout not confirmed, or still pending). */
    BillingUnavailableReasonEnum["NOT_LINKED"] = "NOT_LINKED";
    /** The provider call failed or timed out. Retryable. */
    BillingUnavailableReasonEnum["PROVIDER_UNAVAILABLE"] = "PROVIDER_UNAVAILABLE";
    /** The provider answered but the object was missing or not ours. */
    BillingUnavailableReasonEnum["PROVIDER_MISMATCH"] = "PROVIDER_MISMATCH";
    /** The business's provider has no supported live read in MVP1. */
    BillingUnavailableReasonEnum["NOT_SUPPORTED"] = "NOT_SUPPORTED";
})(BillingUnavailableReasonEnum || (BillingUnavailableReasonEnum = {}));
export const BillingUnavailableReasonSchema = z.nativeEnum(BillingUnavailableReasonEnum);
/**
 * A successful live read. `nextChargeAt` is an estimate derived from
 * `currentPeriodEnd` and is `null` whenever the subscription will not renew.
 */
export const LiveRecurringSummaryAvailableSchema = z
    .object({
    available: z.literal(true),
    provider: PublicExperienceProviderSchema,
    subscriptionId: StripeSubscriptionIdSchema,
    status: LiveRecurringStatusSchema,
    amountCents: PositiveMoneyCentsSchema,
    currency: PublicExperienceCurrencySchema,
    interval: BillingIntervalSchema,
    intervalCount: z.number().int().min(1).max(52),
    currentPeriodEnd: IsoTimestampSchema.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: IsoTimestampSchema.nullable(),
    lastInvoiceStatus: LastInvoiceStatusSchema.nullable(),
    /** Estimated next charge. `null` when cancelling or not renewing. */
    nextChargeAt: IsoTimestampSchema.nullable(),
    /** When the provider was read, so a stale panel is recognizable. */
    readAt: IsoTimestampSchema,
})
    .strict();
/**
 * A failed or inapplicable live read. Explicitly NOT a status — the UI must say
 * "billing details unavailable", never "cancelled".
 */
export const LiveRecurringSummaryUnavailableSchema = z
    .object({
    available: z.literal(false),
    reason: BillingUnavailableReasonSchema,
    /** Operator-facing detail. Safe for admin display; never contains PII. */
    message: z.string().max(500).nullable().default(null),
    retryable: z.boolean(),
    readAt: IsoTimestampSchema,
})
    .strict();
/** Discriminated on `available` so consumers cannot read amounts off a failure. */
export const LiveRecurringSummarySchema = z.discriminatedUnion('available', [
    LiveRecurringSummaryAvailableSchema,
    LiveRecurringSummaryUnavailableSchema,
]);
export const isBillingSummaryAvailable = (summary) => summary.available;
